import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Phone, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, validEmail, waLink } from "@/lib/api";
import { useSettings, useSeo } from "@/context/SettingsContext";
import { PageHero } from "@/components/site/Blocks";
import { Field, inputCls, textareaCls, Btn } from "@/components/common";
import { ContactForm } from "@/components/DynamicFields";

function PrivacyForm() {
  const [f, setF] = useState({ name: "", email: "", phone: "", request_type: "deletion", details: "", company_website: "" });
  const [id, setId] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (f.name.trim().length < 2 || !validEmail(f.email)) return toast.error("Please enter your name and a valid email");
    setLoading(true);
    try { const r = await api.post("/public/privacy-request", f); setId(r.data.request_id); } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  if (id) return <div data-testid="privacy-request-success" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-emerald-900">Your request has been logged (ref {id}). We may contact you to verify your identity before processing it.</div>;
  return (
    <form onSubmit={submit} noValidate className="grid gap-5 sm:grid-cols-2" data-testid="privacy-request-form">
      <input name="company_website" value={f.company_website} onChange={set("company_website")} tabIndex={-1} autoComplete="off" className="absolute -left-[9999px] opacity-0" aria-hidden="true" />
      <Field label="Name" required><input value={f.name} onChange={set("name")} data-testid="privacy-name-input" className={inputCls} /></Field>
      <Field label="Email used with us" required><input type="email" value={f.email} onChange={set("email")} data-testid="privacy-email-input" className={inputCls} /></Field>
      <Field label="Request type" className="sm:col-span-2">
        <select value={f.request_type} onChange={set("request_type")} data-testid="privacy-type-select" className={inputCls}>
          <option value="deletion">Delete my personal data</option><option value="access">Send me a copy of my data</option><option value="correction">Correct my data</option><option value="opt_out_marketing">Stop marketing messages</option>
        </select>
      </Field>
      <Field label="Details" className="sm:col-span-2"><textarea value={f.details} onChange={set("details")} maxLength={2000} className={textareaCls} /></Field>
      <Btn type="submit" variant="dark" loading={loading} data-testid="privacy-submit-button" className="sm:col-span-2 sm:w-fit">Submit privacy request</Btn>
    </form>
  );
}

export default function Contact() {
  const { settings } = useSettings();
  const { hash } = useLocation();
  useSeo("Contact Us", "Contact our team about lawn mower home trials.");
  useEffect(() => { if (hash) setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 100); }, [hash]);
  const tiles = [
    settings.phone && [Phone, "Call us", settings.phone, `tel:${settings.phone.replace(/\D/g, "")}`],
    settings.email && [Mail, "Email", settings.email, `mailto:${settings.email}`],
    settings.whatsapp_number && [MessageCircle, "WhatsApp", "Start a chat", waLink(settings.whatsapp_number, settings.whatsapp_message)],
  ].filter(Boolean);
  return (
    <>
      <PageHero eyebrow="Contact" title="Talk to a real person." text="Questions about a mower, your lawn or the trial? Send us a message and our team will reply." />
      <section className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-12 lg:px-8">
        <div className="space-y-4 lg:col-span-4">
          {tiles.map(([I, t, v, href]) => (
            <a key={t} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={`contact-tile-${t.toLowerCase().replace(/\s/g, "-")}`} className="flex items-center gap-4 rounded-2xl border border-slate-200 p-5 transition-colors hover:border-emerald-600">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-emerald-50 text-emerald-700"><I className="h-5 w-5" /></span>
              <span><span className="block text-sm text-slate-500">{t}</span><span className="font-semibold text-slate-900">{v}</span></span>
            </a>
          ))}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-10 lg:col-span-8"><h2 className="mb-6 font-display text-2xl font-bold text-slate-900">Send us a message</h2><ContactForm /></div>
      </section>
      <section id="privacy-request" className="bg-slate-50 py-16 sm:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <h2 className="font-display text-2xl font-bold text-slate-900 sm:text-3xl">Privacy & data deletion requests</h2>
          <p className="mt-3 text-slate-600">Ask us to access, correct or delete the personal information we hold about you, or to stop marketing.</p>
          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 sm:p-10"><PrivacyForm /></div>
        </div>
      </section>
    </>
  );
}
